import * as Sentry from "@sentry/react";
import { SSE } from "sse.js";

import { safeJsonParse } from "../components/global/JsonHelper";

export const SSE_EVENT_TYPES = Object.freeze({
  HEARTBEAT: "1",
  NOTIFICATION_SUMMARY: "2",
  CALL_SUMMARY: "3",
  SESSION_EXPIRY: "4",
  DATA_READY: "5",
});

const CONNECTION_EVENT_NAMES = ["open", "error", "abort"];

const normalizeUrl = (value) => {
  if (!value) {
    return null;
  }

  const trimmedValue = value.trim().replace(/\/+$/, "");

  if (!trimmedValue) {
    return null;
  }

  if (/^https?:\/\//i.test(trimmedValue)) {
    return trimmedValue;
  }

  return `https://${trimmedValue}`;
};

const normalizeNotificationData = (value) => {
  if (!value) {
    return null;
  }

  if (typeof value === "object") {
    return value;
  }

  if (typeof value === "string") {
    return safeJsonParse(value, null);
  }

  return null;
};

export default class ServerSentEventsService {
  EVENT_TYPES = SSE_EVENT_TYPES;

  constructor() {
    this.accessToken = null;
    this.baseUrl = normalizeUrl(process.env.REACT_APP_SSE_URL);
    this.source = null;
    this.listeners = new Map();
    this.connectionListeners = CONNECTION_EVENT_NAMES.reduce((acc, name) => {
      acc[name] = new Set();
      return acc;
    }, {});

    this.handleMessage = this.handleMessage.bind(this);
    this.handleOpen = this.handleOpen.bind(this);
    this.handleError = this.handleError.bind(this);
    this.handleAbort = this.handleAbort.bind(this);
  }

  get readyState() {
    return this.source?.readyState ?? SSE.CLOSED;
  }

  get isConnected() {
    return this.readyState === SSE.OPEN;
  }

  setAccessToken(token) {
    this.accessToken = token;
    return this;
  }

  async _checkAuth() {
    try {
      const base = process.env.REACT_APP_API_URL;
      if (!base) { return true; }
      const normalizedBase = base.endsWith("/") ? base : `${base}/`;
      const response = await fetch(`${normalizedBase}api/users/me`, {
        method: "GET",
        headers: {
          Authorization: `Token ${this.accessToken}`,
          "Content-Type": "application/json",
        },
      });
      return response.status !== 401;
    } catch {
      return true;
    }
  }

  buildUrl(params = {}) {
    try {
      const url = new URL(this.baseUrl);
      Object.entries(params || {}).forEach(([key, value]) => {
        if (value === undefined || value === null || value === "") {
          return;
        }
        url.searchParams.set(key, value);
      });
      return url.toString();
    } catch (error) {
      Sentry.captureException(error, {
        tags: { scope: "sse-service", stage: "buildUrl" },
      });
      return this.baseUrl;
    }
  }

  async start({ accessToken, query } = {}) {
    if (accessToken) {
      this.setAccessToken(accessToken);
    }

    if (!this.accessToken) {
      throw new Error("SSE connection requires an access token.");
    }

    const isAuthed = await this._checkAuth();
    if (!isAuthed) {
      throw new Error("SSE connection aborted: authentication token is no longer valid.");
    }

    if (this.source) {
      return this.source;
    }

    const targetUrl = this.buildUrl(query);

    this.source = new SSE(targetUrl, {
      headers: {
        Authorization: `Token ${this.accessToken}`,
        Accept: "text/event-stream",
      },
      withCredentials: false,
      autoReconnect: true,
      reconnectDelay: 5000,
      maxRetries: null,
      useLastEventId: true,
      start: false,
    });

    this.source.addEventListener("open", this.handleOpen);
    this.source.addEventListener("error", this.handleError);
    this.source.addEventListener("abort", this.handleAbort);
    this.source.addEventListener("message", this.handleMessage);

    Object.values(SSE_EVENT_TYPES).forEach((eventName) => {
      this.source.addEventListener(eventName, this.handleMessage);
    });

    this.source.stream();

    return this.source;
  }

  stop() {
    if (!this.source) {
      return;
    }

    this.source.removeEventListener("open", this.handleOpen);
    this.source.removeEventListener("error", this.handleError);
    this.source.removeEventListener("abort", this.handleAbort);
    this.source.removeEventListener("message", this.handleMessage);

    Object.values(SSE_EVENT_TYPES).forEach((eventName) => {
      this.source.removeEventListener(eventName, this.handleMessage);
    });

    this.source.close();
    this.source = null;
  }

  reconnect(options = {}) {
    this.stop();
    return this.start(options);
  }

  on(eventName, handler) {
    if (!eventName || typeof handler !== "function") {
      return () => {};
    }

    const normalizedName = String(eventName);

    if (!this.listeners.has(normalizedName)) {
      this.listeners.set(normalizedName, new Set());
    }

    const handlers = this.listeners.get(normalizedName);
    handlers.add(handler);

    return () => this.off(normalizedName, handler);
  }

  off(eventName, handler) {
    const normalizedName = String(eventName);
    const handlers = this.listeners.get(normalizedName);

    if (!handlers) {
      return;
    }

    handlers.delete(handler);

    if (!handlers.size) {
      this.listeners.delete(normalizedName);
    }
  }

  onHeartbeat(handler) {
    return this.on(SSE_EVENT_TYPES.HEARTBEAT, handler);
  }

  onNotificationSummary(handler) {
    return this.on(SSE_EVENT_TYPES.NOTIFICATION_SUMMARY, handler);
  }

  onCallSummary(handler) {
    return this.on(SSE_EVENT_TYPES.CALL_SUMMARY, handler);
  }

  onSessionExpiry(handler) {
    return this.on(SSE_EVENT_TYPES.SESSION_EXPIRY, handler);
  }

  onConnection(eventName, handler) {
    if (!CONNECTION_EVENT_NAMES.includes(eventName) || typeof handler !== "function") {
      return () => {};
    }

    const listeners = this.connectionListeners[eventName];
    listeners.add(handler);

    return () => listeners.delete(handler);
  }

  emit(eventName, payload, rawEvent) {
    const handlers = this.listeners.get(eventName);

    if (!handlers || !handlers.size) {
      return;
    }

    handlers.forEach((handler) => {
      try {
        handler(payload, rawEvent);
      } catch (error) {
        Sentry.captureException(error, {
          tags: { scope: "sse-service", event: eventName },
        });
      }
    });
  }

  emitConnection(eventName, event) {
    const handlers = this.connectionListeners[eventName];

    if (!handlers || !handlers.size) {
      return;
    }

    handlers.forEach((handler) => {
      try {
        handler(event);
      } catch (error) {
        Sentry.captureException(error, {
          tags: { scope: "sse-service", event: eventName },
        });
      }
    });
  }

  handleOpen(event) {
    this.emitConnection("open", event);
  }

  handleError(event) {
    if (event.responseCode === 401) {
      this.stop();
    }
    this.emitConnection("error", event);
  }

  handleAbort(event) {
    this.emitConnection("abort", event);
  }

  handleMessage(event) {
    const parsedPayload = this.parseEventPayload(event?.data);
    const eventType = event?.type || parsedPayload?.type || "message";
    const normalizedPayload = this.normalizePayload(eventType, parsedPayload);

    this.emit(eventType, normalizedPayload ?? parsedPayload ?? event?.data, event);

    if (eventType !== "message") {
      this.emit("message", normalizedPayload ?? parsedPayload ?? event?.data, event);
    }
  }

  parseEventPayload(raw) {
    if (raw === undefined || raw === null) {
      return null;
    }

    if (typeof raw !== "string") {
      return raw;
    }

    const trimmed = raw.trim();
    if (!trimmed) {
      return null;
    }

    return safeJsonParse(trimmed, trimmed);
  }

  normalizePayload(eventType, payload) {
    if (!payload || typeof payload !== "object") {
      return payload;
    }

    switch (eventType) {
      case SSE_EVENT_TYPES.HEARTBEAT:
        return {
          message: payload.message || "",
          timestamp: payload.timestamp || null,
          type: SSE_EVENT_TYPES.HEARTBEAT,
        };
      case SSE_EVENT_TYPES.NOTIFICATION_SUMMARY:
        return {
          message: payload.message || "",
          timestamp: payload.timestamp || null,
          notificationData: normalizeNotificationData(payload.notification_data),
          type: SSE_EVENT_TYPES.NOTIFICATION_SUMMARY,
        };
      case SSE_EVENT_TYPES.CALL_SUMMARY:
        return {
          message: payload.message || "",
          timestamp: payload.timestamp || null,
          callSummary: normalizeNotificationData(payload.call_summary),
          type: SSE_EVENT_TYPES.CALL_SUMMARY,
        };
      case SSE_EVENT_TYPES.SESSION_EXPIRY:
        return {
          message: payload.message || "",
          timestamp: payload.timestamp || null,
          expiry: payload.expiry ?? null,
          type: SSE_EVENT_TYPES.SESSION_EXPIRY,
        };
      default:
        return payload;
    }
  }
}
